import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { MarketingChrome } from "../components/MarketingChrome";
import { IconBack, IconLock } from "../components/Icons";
import { auth as authApi } from "../api/endpoints";
import { validateEmail, validateName } from "../lib/validation";

/**
 * Starting a reset. The API answers the same way whether or not the address
 * has an account, so the confirmation never says which one it was.
 */
export function ForgotPassword() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const nameProblem = validateName(name);
    const problem = validateEmail(email);
    setNameError(nameProblem);
    setError(problem);
    setSubmitError(null);
    if (nameProblem || problem) {
      setDone(false);
      return;
    }

    setSubmitting(true);
    try {
      await authApi.forgotPassword(email.trim());
      setDone(true);
    } catch (err) {
      setDone(false);
      setSubmitError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <MarketingChrome back={{ to: "/", label: "Back to Inputry", short: "Back" }} footer="compact">
      <div className={done ? "narrow-page forgot-page is-sent" : "narrow-page forgot-page"}>
        <div className="panel-card reset-inbox">
          <div className="kicker"><i /> Contributor network</div>
          <div className="inbox-check" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M3.5 9.2 7.1 12.8 14.5 5.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h1>Check your inbox</h1>
          <p className="sub">
            We've sent password reset instructions if an Inputry account exists for that email address.
          </p>
          <Link to="/login" className="btn btn-primary">Back to sign in</Link>
          <p className="fine">Didn't get it? Check your spam folder or try again in a few minutes.</p>
        </div>
        <form className="panel-card reset-password-card" onSubmit={handleSubmit} noValidate>
          <div className="kicker"><i /> Contributor network</div>
          <div className="icon-tile"><IconLock /></div>
          <h1>Reset your password</h1>
          <p className="sub">Enter the email address linked to your Inputry contributor account.</p>
          <div className={nameError ? "field err" : "field"}>
            <label htmlFor="reset-name">Full name</label>
            <input
              id="reset-name"
              value={name}
              placeholder="Your full name"
              autoComplete="name"
              maxLength={20}
              onChange={(event) => {
                setName(event.target.value);
                setNameError(null);
                setSubmitError(null);
                if (done) setDone(false);
              }}
            />
            <div className="err-msg">{nameError}</div>
          </div>
          <div className={error ? "field err" : "field"}>
            <label htmlFor="reset-email">Email address</label>
            <input
              id="reset-email"
              type="email"
              value={email}
              placeholder="Enter email"
              onChange={(event) => {
                setEmail(event.target.value);
                setError(null);
                setSubmitError(null);
                if (done) setDone(false);
              }}
            />
            <div className="err-msg">{error}</div>
          </div>
          {submitError && <div className="form-error">{submitError}</div>}
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? "Sending…" : "Send reset link"}
          </button>
          {done && (
            <div className="reset-sent" role="status">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3.5" y="5.5" width="17" height="13" rx="2.2" stroke="currentColor" strokeWidth="1.7" />
                <path d="M4.5 7.5 12 13l7.5-5.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div>
                <strong>Check your inbox</strong>
                <p>We've sent instructions if an account exists.</p>
              </div>
            </div>
          )}
          <p className="fine">
            <Link to="/login"><IconBack width={14} height={14} /> Back to sign in</Link>
          </p>
        </form>
      </div>
    </MarketingChrome>
  );
}
